import {EBreakpoint} from '@/common/scripts/constants';
import {throttle} from 'throttle-debounce';
import {Utils} from '../../../common/scripts/utils';

class FileUploader {
    private wrapper: HTMLElement;
    private fileInput: HTMLInputElement;
    private previewContainer: HTMLElement;
    private filesGallery: HTMLElement;
    private filesGalleryWrapper: HTMLElement;
    private filesGalleryClose: HTMLElement;
    private fileCounter: HTMLElement;
    private files: File[] = [];
    private currentPreviewCounter: number;
    private limit: number;
    private maxSize: number;
    private acceptFile: string;
    private errorWrapper: HTMLElement;

    init(wrapper: HTMLElement): void {
        this.wrapper = wrapper;

        if (!this.wrapper) {
            return;
        }

        this.setElements();
        this.bindEvents();
        this.initGallery();
        this.initResizeListener();
    }

    private setElements(): void {
        this.fileInput = this.wrapper.querySelector('.j-input-file input') as HTMLInputElement;
        this.previewContainer = this.wrapper.querySelector('.j-input-file-preview') as HTMLElement;
        this.filesGallery = this.wrapper.querySelector('.j-input-file-gallery') as HTMLElement;
        this.filesGalleryWrapper = this.wrapper.querySelector('.j-input-file-gallery-wrapper') as HTMLElement;
        this.filesGalleryClose = this.wrapper.querySelector('.j-input-file-gallery-close') as HTMLElement;
        this.fileCounter = this.wrapper.querySelector('.j-input-file-counter') as HTMLElement;
        this.errorWrapper = this.wrapper.querySelector('.j-input-file-error') as HTMLElement;
        this.limit = Number(this.fileInput.dataset['limit'] || 15);
        this.maxSize = Number(this.fileInput.dataset['size'] || 5);
        this.acceptFile = this.fileInput.getAttribute('accept') || '.jpg, .jpeg, .png, .pdf';
    }

    private bindEvents(): void {
        this.fileInput.addEventListener('change', this.handleFileChange.bind(this));
    }

    private handleFileChange(): void {
        const newFiles = Array.from(this.fileInput.files || []);

        this.errorWrapper.innerHTML = '';

        this.files.push(...newFiles);

        this.checkFormat();
        this.checkSize();
        this.checkLimit();
        this.updatePreview();
        this.updateCounter();
        this.updateFileInput();
    }

    private checkFormat(): void {
        // Проверяем на соответствие типу
        const accept = this.acceptFile;
        // eslint-disable-next-line
        const typeRegex = new RegExp(accept.replace(/\s/g, '').replace(/\*/g, '.\*').replace(/,/g, '|' ));
        const startSize = this.files.length;

        this.files = this.files.reduce((result: File[], file: File) => {
            if (file !== null && typeRegex.test(file.type)) {
                result.push(file);
            }

            return result;
        }, []);

        if (this.files.length !== startSize) {
            this.createError(`Доступные форматы файлов: ${this.acceptFile}`);
        }
    }

    private checkLimit(): void {
        if (this.files.length > this.limit) {
            this.files.length = this.limit;
            this.createError(`Максимальное количество файлов ${this.limit}`);
        }
    }

    private checkSize(): void {
        const size = this.maxSize * 1024 * 1024;
        const startSize = this.files.length;

        this.files = this.files.reduce((result: File[], file: File) => {
            if (file !== null && file.size <= size) {
                result.push(file);
            }

            return result;
        }, []);

        if (this.files.length !== startSize) {
            this.createError(`Максимально допустимый размер файла ${this.maxSize} мб.`);
        }
    }

    private createError(errorText: string): void {
        const errorItem = document.createElement('div');

        errorItem.classList.add('input-file__error-text');
        errorItem.innerText = errorText;
        this.errorWrapper.appendChild(errorItem);
    }

    private updatePreview(): void {
        this.previewContainer.innerHTML = '';
        this.filesGallery.innerHTML = '';

        this.files.forEach((file: File, index: number) => {
            if (file?.type.startsWith('image/')) {
                const reader = new FileReader();

                reader.onload = (element: ProgressEvent <FileReader>) => {
                    const img = this.createImage(element.target?.result as string, file.name, index);

                    this.previewContainer.appendChild(this.createPreviewItem(img, index));
                    this.filesGallery
                        .appendChild(this.createGalleryItem(img.cloneNode(true) as HTMLImageElement, index));
                };

                reader.readAsDataURL(file);
            }

            if (file?.type.startsWith('application/')) {
                const reader = new FileReader();

                reader.onload = () => {
                    const name = this.createPdf(file.name, index);

                    this.previewContainer.appendChild(this.createPdfPreviewItem(name, index));
                    this.filesGallery
                        .appendChild(this.createPdfGalleryItem(name.cloneNode(true) as HTMLImageElement, index));
                };

                reader.readAsDataURL(file);
            }
        });
    }

    private createImage(src: string, alt: string, index: number): HTMLImageElement {
        const img = document.createElement('img');

        img.src = src;
        img.alt = alt;
        img.dataset['index'] = index.toString();

        return img;
    }

    private createPdf(name: string, index: number): HTMLElement {
        const pdf = document.createElement('div');
        const icon = '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="22" viewBox="0 0 18 22" fill="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M10.25 1.8047C10.019 1.7544 9.74643 1.75 9.01178 1.75H5.8C4.94755 1.75 4.35331 1.75058 3.89068 1.78838C3.4368 1.82547 3.17604 1.8946 2.97852 1.99524C2.55516 2.21095 2.21095 2.55516 1.99524 2.97852C1.8946 3.17604 1.82547 3.43681 1.78838 3.89068C1.75058 4.35331 1.75 4.94755 1.75 5.8V16.2C1.75 17.0525 1.75058 17.6467 1.78838 18.1093C1.82547 18.5632 1.8946 18.824 1.99524 19.0215C2.21095 19.4448 2.55516 19.7891 2.97852 20.0048C3.17604 20.1054 3.4368 20.1745 3.89068 20.2116C4.35331 20.2494 4.94755 20.25 5.8 20.25H12.2C13.0525 20.25 13.6467 20.2494 14.1093 20.2116C14.5632 20.1745 14.824 20.1054 15.0215 20.0048C15.4448 19.7891 15.789 19.4448 16.0048 19.0215C16.1054 18.824 16.1745 18.5632 16.2116 18.1093C16.2494 17.6467 16.25 17.0525 16.25 16.2V8.98823C16.25 8.25364 16.2456 7.98111 16.1953 7.75007L12.5738 7.75007C12.3163 7.75009 12.0798 7.75011 11.8821 7.73396C11.669 7.71654 11.4358 7.67668 11.2055 7.55933C10.8762 7.39156 10.6085 7.12384 10.4407 6.79456C10.3234 6.56425 10.2835 6.33108 10.2661 6.11795C10.25 5.92026 10.25 5.68379 10.25 5.42631L10.25 1.8047ZM11.2966 0.580464C11.0832 0.484414 10.861 0.408404 10.6328 0.353614C10.1998 0.249666 9.7488 0.249797 9.09724 0.249985C9.06913 0.249993 9.04065 0.250002 9.01178 0.250002L5.7679 0.250001C4.95506 0.249994 4.29944 0.249988 3.76853 0.293365C3.2219 0.338027 2.74175 0.432387 2.29754 0.658727C1.59193 1.01825 1.01825 1.59193 0.658726 2.29754C0.432385 2.74176 0.338026 3.2219 0.293364 3.76853C0.249987 4.29944 0.249993 4.95506 0.25 5.7679V16.2321C0.249993 17.0449 0.249987 17.7006 0.293364 18.2315C0.338026 18.7781 0.432385 19.2582 0.658726 19.7025C1.01825 20.4081 1.59193 20.9818 2.29754 21.3413C2.74175 21.5676 3.2219 21.662 3.76853 21.7066C4.29944 21.75 4.95505 21.75 5.76788 21.75H12.2321C13.045 21.75 13.7006 21.75 14.2315 21.7066C14.7781 21.662 15.2582 21.5676 15.7025 21.3413C16.4081 20.9818 16.9817 20.4081 17.3413 19.7025C17.5676 19.2582 17.662 18.7781 17.7066 18.2315C17.75 17.7006 17.75 17.045 17.75 16.2321V8.98823C17.75 8.95936 17.75 8.93087 17.75 8.90277C17.7502 8.25121 17.7503 7.8002 17.6464 7.36722C17.5918 7.13972 17.5161 6.91816 17.4204 6.70539C17.416 6.69497 17.4113 6.68467 17.4064 6.6745C17.3446 6.54027 17.2748 6.40963 17.1974 6.28327C16.9647 5.90361 16.6457 5.58479 16.1849 5.12419C16.165 5.10432 16.1449 5.08419 16.1244 5.06378L12.9362 1.87556C12.9158 1.85514 12.8957 1.835 12.8758 1.81512C12.4152 1.35426 12.0964 1.03526 11.7167 0.802601C11.5898 0.724831 11.4586 0.6548 11.3237 0.592809C11.3148 0.58852 11.3057 0.584404 11.2966 0.580464ZM11.75 2.81083V5.40007C11.75 5.69248 11.7506 5.86667 11.7611 5.9958C11.7676 6.0746 11.7761 6.10773 11.7788 6.11661C11.8023 6.16125 11.8388 6.19773 11.8835 6.22125C11.8923 6.224 11.9255 6.2325 12.0043 6.23894C12.1334 6.24949 12.3076 6.25007 12.6 6.25007H15.1892C15.1497 6.2104 15.1079 6.16859 15.0638 6.12444L11.8756 2.93622C11.8314 2.8921 11.7897 2.85036 11.75 2.81083ZM11.8809 6.22035C11.8809 6.22033 11.8814 6.22051 11.8824 6.22091ZM11.7797 6.1192C11.7797 6.11923 11.7795 6.11876 11.7792 6.11768ZM4.25 12C4.25 11.5858 4.58579 11.25 5 11.25H13C13.4142 11.25 13.75 11.5858 13.75 12C13.75 12.4142 13.4142 12.75 13 12.75H5C4.58579 12.75 4.25 12.4142 4.25 12ZM4.25 16C4.25 15.5858 4.58579 15.25 5 15.25H11C11.4142 15.25 11.75 15.5858 11.75 16C11.75 16.4142 11.4142 16.75 11 16.75H5C4.58579 16.75 4.25 16.4142 4.25 16Z"/></svg>';
        let fileName = name;

        if (fileName.length > 14) {
            fileName = `${name.slice(0, 14)}...`;
        }

        pdf.classList.add('input-file__pdf-info');
        pdf.innerHTML = `${icon} <span>${fileName}</span>`;
        pdf.dataset['index'] = index.toString();

        return pdf;
    }

    private createPreviewItem(img: HTMLImageElement, index: number): HTMLElement {
        const previewItem = document.createElement('div');

        previewItem.classList.add('input-file__preview-item');
        previewItem.dataset['index'] = index.toString();
        previewItem.appendChild(img);
        previewItem.addEventListener('click', () => {
            return this.removeFile(index);
        });

        return previewItem;
    }

    private createGalleryItem(img: HTMLImageElement, index: number): HTMLElement {
        const galleryItem = document.createElement('div');

        galleryItem.classList.add('input-file__gallery-item');
        galleryItem.dataset['index'] = index.toString();
        galleryItem.appendChild(img);
        galleryItem.addEventListener('click', () => {
            return this.removeFile(index);
        });

        return galleryItem;
    }

    private createPdfPreviewItem(pdf: HTMLElement, index: number): HTMLElement {
        const previewItem = document.createElement('div');

        previewItem.className = 'input-file__preview-item input-file__preview-item_theme_pdf';
        previewItem.dataset['index'] = index.toString();
        previewItem.appendChild(pdf);
        previewItem.addEventListener('click', () => {
            return this.removeFile(index);
        });

        return previewItem;
    }

    private createPdfGalleryItem(pdf: HTMLElement, index: number): HTMLElement {
        const galleryItem = document.createElement('div');

        galleryItem.className = 'input-file__gallery-item input-file__gallery-item_theme_pdf';
        galleryItem.dataset['index'] = index.toString();
        galleryItem.appendChild(pdf);
        galleryItem.addEventListener('click', () => {
            return this.removeFile(index);
        });

        return galleryItem;
    }

    private removeFile(index: number): void {
        this.files[index] = null as unknown as File;
        this.updateFileInput();
        this.updateCounter();
        this.removeDomElement(index);
    }

    private removeDomElement(index: number): void {
        const selector = `[data-index="${index}"]`;

        this.filesGallery.querySelector(selector)?.remove();
        this.previewContainer.querySelector(selector)?.remove();
    }

    private updateCounter(): void {
        this.currentPreviewCounter = this.getPreviewCounter();
        const count = this.files.filter((file: File) => {
            return file !== null;
        }).length;

        this.fileCounter.classList.toggle('is-hide', count <= this.currentPreviewCounter);
        this.fileCounter.innerText = count > this.currentPreviewCounter ? `+${count - this.currentPreviewCounter}` : '';

        if (count === 0) {
            this.filesGalleryWrapper.classList.remove('is-active');
            Utils.bodyStatic();
        }
    }

    private getPreviewCounter(): number {
        if (window.innerWidth >= EBreakpoint.DESKTOP) {
            return 4;
        }
        if (window.innerWidth >= EBreakpoint.TABLET) {
            return 3;
        }

        return 2;
    }

    private initGallery(): void {
        this.fileCounter.addEventListener('click', () => {
            this.filesGalleryWrapper.classList.add('is-active');
            Utils.bodyFixed(this.filesGalleryWrapper);
        });
        this.filesGalleryClose.addEventListener('click', () => {
            this.filesGalleryWrapper.classList.remove('is-active');
            Utils.bodyStatic();
        });
    }

    private initResizeListener(): void {
        window.addEventListener('resize', throttle(50, () => {
            return this.updateCounter();
        }));
    }

    private updateFileInput(): void {
        const dataTransfer = new DataTransfer();

        this.files.filter((file: File) => {
            return file !== null;
        }).forEach((file: File) => {
            return dataTransfer.items.add(file);
        });
        this.fileInput.files = dataTransfer.files;
        this.fileInput.classList.toggle('is-active', this.files.some((file: File) => {
            return file !== null;
        }));
    }
}

export default FileUploader;
